// Pre-render analysis of a MiniApp OpenUI Lang program (AGENT-731, OSA-09).
// The chatserver compiler does not enforce the Aurora rules yet; the OS
// renderer applies them and reports each deviation:
//   - several primary actions: only the first one, in tree order, keeps the
//     accent, the others render as secondary;
//   - a free colour property: ignored (no catalogue component reads it).
import { createParser, type ElementNode } from '@openuidev/react-lang'
import { miniAppOpenUILibrary } from './library'
import { claimsPrimary, primaryActionKey, type ActionBarAction } from './miniAppRules'

export type ProgramGuardResult = {
  /** Key of the single action rendered with the accent, or null. */
  primaryKey: string | null
  warnings: string[]
}

const COLOUR_KEY = /^(?:colou?r|bg|bgColou?r|background(?:Colou?r)?|textColou?r|borderColou?r|fill|stroke|hex|rgb|hsl|tint|palette)$/i

let parser: ReturnType<typeof createParser> | null = null
function getParser() {
  parser ??= createParser(miniAppOpenUILibrary.toJSONSchema())
  return parser
}

function isElement(value: unknown): value is ElementNode {
  return Boolean(value) && typeof value === 'object' && (value as { type?: unknown }).type === 'element'
}

export function analyzeProgram(program: string | null | undefined): ProgramGuardResult {
  const result: ProgramGuardResult = { primaryKey: null, warnings: [] }
  if (!program) return result
  let root: ElementNode | null = null
  try {
    root = getParser().parse(program).root
  } catch {
    return result
  }
  const primaries: ActionBarAction[] = []

  const visit = (value: unknown, path: string) => {
    if (isElement(value)) {
      const here = `${path}${path ? '.' : ''}${value.typeName}`
      if (value.typeName === 'ActionBarBlock' && Array.isArray(value.props.actions)) {
        for (const action of value.props.actions as ActionBarAction[]) {
          if (action && typeof action === 'object' && claimsPrimary(action)) primaries.push(action)
        }
      }
      for (const [key, prop] of Object.entries(value.props ?? {})) visit(prop, `${here}.${key}`)
      return
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, `${path}[${index}]`))
      return
    }
    if (value && typeof value === 'object') {
      for (const [key, prop] of Object.entries(value as Record<string, unknown>)) {
        if (COLOUR_KEY.test(key)) {
          result.warnings.push(`free colour property ignored at ${path}.${key}: MiniApps only carry semantic tones (tone, trend, chart_type)`)
          continue
        }
        visit(prop, `${path}.${key}`)
      }
    }
  }
  visit(root, '')

  if (primaries.length > 0) result.primaryKey = primaryActionKey(primaries[0])
  if (primaries.length > 1) {
    result.warnings.push(
      `${primaries.length} primary actions declared (${primaries.map((a) => `"${a.label}"`).join(', ')}): only "${primaries[0].label}" keeps the accent, one primary action per screen`,
    )
  }
  return result
}
